import { Injectable } from '@angular/core';
import { BehaviorSubject, fromEvent } from 'rxjs';

/**
 * Many components can share a stardard breakpoint for when the browser switches from
 * 'everything mode' to 'minimal mode' however not all components will use the same
 * breakpoint. But since this is a common one we can declare it for re-use here.
 * @date 9/25/2022 - 11:19:57 AM
 *
 * @type {number}
 */
export const STANDARD_BREAK_POINT: number = 950;

/**
 * Small helper function to get the lesser of the window width values.
 *
 * If the inner window is too wide and has caused horizontal scrollbars to appear
 * we want the outerWidth value. On the other hand if we are a developer and we
 * have our dev tools open and are resizing the non-dev-tools part of chrome
 * we want the innerWidth value as the outerWidth value includes the dev
 * tools which we dont want for responsiveness testing.
 * @date 9/25/2022 - 11:20:03 AM
 *
 * @returns {number}
 */
function getWindowWidth(): number {
	return window.outerWidth < window.innerWidth ? window.outerWidth : window.innerWidth;
}

/**
 * Gets the scroll position of the window.
 * @date 9/25/2022 - 11:20:10 AM
 *
 * @returns {number}
 */
function getWindowScrollPosition(): number {
	return window.scrollY;
}

@Injectable({
	providedIn: 'root',
})
export class BrowserService {
	//The initial value
	public onResize$ = new BehaviorSubject<number>(getWindowWidth());
	public onScroll$ = new BehaviorSubject<number>(getWindowScrollPosition());
	public isMobile: boolean = false;
	public onMobile$ = new BehaviorSubject<boolean>(this.isMobile);
	public width: number = getWindowWidth();

	constructor() {
		//Listen for resize events, debounce them, then update
		//subscribers at the specified interval. This will most
		//likely be used to implement responsive behavior.
		fromEvent(window, 'resize').subscribe((event: any) => this.resizeTick());
		fromEvent(window, 'scroll').subscribe((event: any) => this.scrollTick());

		//Kick start things
		requestAnimationFrame(() => {
			this.resizeTick();
			this.scrollTick();
		});
	}

	/**
	 * Detect if dom element is above the viewport
	 * @date 9/25/2022 - 11:20:18 AM
	 *
	 * @public
	 * @param {HTMLElement} element
	 * @returns {boolean}
	 */
	public isDomElementAboveViewport(element: HTMLElement): boolean {
		const rect = element.getBoundingClientRect();
		return rect.top < 0;
	}

	/**
	 * Detect whether dom element is starting to leave the viewport
	 * @date 9/25/2022 - 11:20:24 AM
	 *
	 * @public
	 * @param {HTMLElement} element
	 * @returns {boolean}
	 */
	public isDomElementEntirelyWithinViewport(element: HTMLElement): boolean {
		const rect = element.getBoundingClientRect();
		return rect.top >= 0 && rect.bottom <= window.innerHeight;
	}

	/**
	 * Scroll a child element into view within its parent.
	 * @date 11/2/2022 - 7:55:05 AM
	 *
	 * @public
	 * @param {HTMLElement} child
	 */
	public scrollElementIntoView(child: HTMLElement): void {
		child.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
	}

	public scrollIntoView(element: HTMLElement, offset: number = 0): void {
		const rect = element.getBoundingClientRect();
		const top = rect.top + window.scrollY;
		window.scrollTo({
			top: top - offset,
			behavior: 'smooth',
		});
	}

	/**
	 * Executes at boot up and whenever the window is resized
	 * @date 9/25/2022 - 11:20:30 AM
	 *
	 * @private
	 */
	private resizeTick() {
		this.isMobile = getWindowWidth() < STANDARD_BREAK_POINT;
		this.onMobile$.next(this.isMobile);
		this.onResize$.next(getWindowWidth());

		//Update the width
		this.width = getWindowWidth();
	}

	/**
	 * Executes at boot up and whenever the window is scrolled
	 * @date 9/25/2022 - 11:20:36 AM
	 *
	 * @private
	 */
	private scrollTick() {
		this.onScroll$.next(getWindowScrollPosition());
	}
}
